import Api from "@/services/api";
import {
  LOAD_ALL_SUBSCRIPTION_PLANS,
  LOAD_SUBSCRIPTION_PLAN_BY_ID,
  CREATE_SUBSCRIPTION_PLAN,
  DELETE_SUBSCRIPTION_PLAN,
  UPDATE_SUBSCRIPTION_PLAN,
  CREATE_FREE_PLAN,
  LOAD_FREE_PLAN

} from "@/store/_actiontypes";
import {
  SET_ALL_SUBSCRIPTION_PLANS,
  SET_SUBSCRIPTION_PLAN_BY_ID,
  ADD_SUBSCRIPTION_PLAN,
  REMOVE_SUBSCRIPTION_PLAN,
  SET_SUBSCRIPTION_PLANS_ERROR_MESSAGE,
  SET_SUBSCRIPTION_PLANS_SUCCESS_MESSAGE,
  SET_FREE_PLAN_SUCCESS_MESSAGE,
  SET_FREE_PLAN_ERROR_MESSAGE,
  SET_FREE_PLAN_LOADING,
  SET_FREE_PLAN
 
} from "@/store/_mutationtypes";
import { normalizePlan, serializePlan } from "../../helpers/subscriptionPlan";

const state = {
  subscriptionPlans: [],
  selectedSubscriptionPlan: null,
  successMessage: null,
  errorMessage: null,
  loading: false,
  freePlan: [],
  freePlanLoading: false,
  freePlanSuccessMessage: null,
  freePlanErrorMessage: null,
};

const getters = {
  subscriptionPlans: (state) => state.subscriptionPlans,
  subscriptionPlan: (state) => state.selectedSubscriptionPlan,
  subscriptionPlansLoading: (state) => state.loading,
  subscriptionPlansErrorMessage: (state) => state.errorMessage,
  subscriptionPlansSuccessMessage: (state) => state.successMessage,
  freePlan: (state) => state.freePlan,
  freePlanLoading: (state) => state.freePlanLoading,
  freePlanSuccessMessage: (state) => state.freePlanSuccessMessage,
  freePlanErrorMessage: (state) => state.freePlanErrorMessage,
};

const actions = {
  async [LOAD_ALL_SUBSCRIPTION_PLANS]({ commit }, { sortBy = "created_at", sortOrder = "desc",recaptcha_token } = {} ) {
    commit("SET_LOADING", true);

    try {
      const { data: { items = [] } } = await Api.get("subscription", {
        params: {
          sort_by: sortBy,
          sort_order: sortOrder,
        },
        headers: {
              "X-Captcha-Token": recaptcha_token,
            },
      });

      // Normalize API response
      const normalizedPlans = items.map((plan) => (normalizePlan(plan)));

      commit(SET_ALL_SUBSCRIPTION_PLANS, normalizedPlans);
      
      return normalizedPlans;

    } catch (error) {
      commit(SET_SUBSCRIPTION_PLANS_ERROR_MESSAGE, error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async [LOAD_SUBSCRIPTION_PLAN_BY_ID]({ commit }, id) {
    commit("SET_LOADING", true);

    try {
      const { data } = await Api.get(`/subscription/${id}`);
      const normalizedPlan = normalizePlan(data);

      commit(SET_SUBSCRIPTION_PLAN_BY_ID, normalizedPlan);

      return normalizedPlan;
    } catch (error) {
      commit(SET_SUBSCRIPTION_PLANS_ERROR_MESSAGE, error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async [CREATE_SUBSCRIPTION_PLAN]({ commit }, formData) {

    try {
      const payload = serializePlan(formData);
      const { data } = await Api.post("/subscription", payload);

      const normalizedPlan = normalizePlan(data);

      commit(ADD_SUBSCRIPTION_PLAN, normalizedPlan);
      commit(SET_SUBSCRIPTION_PLANS_SUCCESS_MESSAGE, data);

      return normalizedPlan;
    } catch (error) {
      commit(SET_SUBSCRIPTION_PLANS_ERROR_MESSAGE, error);
      throw error;
    }
  },

  async [UPDATE_SUBSCRIPTION_PLAN]({ commit }, formData) {
    try {
      const payload = serializePlan(formData);
      
      const { data } = await Api.put(`/subscription/${formData.id}`, payload);

      commit(SET_SUBSCRIPTION_PLANS_SUCCESS_MESSAGE, data);

    } catch (error) {
      commit(SET_SUBSCRIPTION_PLANS_ERROR_MESSAGE, error);
      throw error;
    }
  },

  async [DELETE_SUBSCRIPTION_PLAN]({ commit }, id) {
    commit("SET_LOADING", true);

    try {
      const { data } = await Api.delete(`/subscription/${id}`);

      commit(REMOVE_SUBSCRIPTION_PLAN, id);
      commit(SET_SUBSCRIPTION_PLANS_SUCCESS_MESSAGE, data);

      return id;
    } catch (error) {
      commit(SET_SUBSCRIPTION_PLANS_ERROR_MESSAGE, error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async [LOAD_FREE_PLAN]({ commit }) {
    commit("SET_FREE_PLAN_LOADING", true);

    try {
      const { data } = await Api.get("free-plan");

      const freePlan = data && typeof data === "object" && Object.keys(data).length > 0? [data]: [];

      commit(SET_FREE_PLAN, freePlan);

      return freePlan;
    } catch (error) {
      commit(SET_FREE_PLAN_ERROR_MESSAGE, error);
      throw error;
    } finally {
      commit("SET_FREE_PLAN_LOADING", false);
    }
  },

  async [CREATE_FREE_PLAN]({ commit }, payload) {
    try {

      const { data } = await Api.post("free-plan", payload);

      commit(SET_FREE_PLAN_SUCCESS_MESSAGE, data);

      return data;

    } catch (error) {
      commit(SET_FREE_PLAN_ERROR_MESSAGE, error);

      throw error;
    }
  },

};


const mutations = {
  SET_LOADING(state, status) {
    state.loading = status;
    },
    [SET_ALL_SUBSCRIPTION_PLANS](state, plans) {
      state.subscriptionPlans = Array.isArray(plans) ? plans : [];
    },
    [SET_SUBSCRIPTION_PLAN_BY_ID](state, plan) {
      state.selectedSubscriptionPlan = plan || null ;
    },
    [ADD_SUBSCRIPTION_PLAN](state, plan) {
      state.subscriptionPlans = [plan, ...state.subscriptionPlans];
    },
    [REMOVE_SUBSCRIPTION_PLAN](state, id) {
      state.subscriptionPlans = state.subscriptionPlans.filter(
        (plan) => plan.id !== id
      );
    },
    [SET_SUBSCRIPTION_PLANS_ERROR_MESSAGE](state, error) {
      state.errorMessage = error.response?.data?.message;
    },
    [SET_SUBSCRIPTION_PLANS_SUCCESS_MESSAGE](state, success) {
      state.successMessage = success?.message;
    },
    [SET_FREE_PLAN_LOADING](state, status) {
      state.freePlanLoading = status;
    },
    [SET_FREE_PLAN](state, freePlan) {
      state.freePlan = Array.isArray(freePlan) ? freePlan : [];
    },
    [SET_FREE_PLAN_SUCCESS_MESSAGE](state, success) {
      state.freePlanSuccessMessage = success?.message;
    },
    [SET_FREE_PLAN_ERROR_MESSAGE](state, error) {
      state.freePlanErrorMessage = error.response?.data?.message;
    }
};


export const subscription_plans = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};